import { Link, Outlet } from "@tanstack/react-router";
import { APP_NAME } from "@shared/constants";
import { OfflineBanner } from "@client/components/OfflineBanner";
import { ThemeToggle } from "@client/components/ThemeToggle";
import { UserMenu } from "@client/components/UserMenu";
import { useAuth } from "@client/hooks/useAuth";

/**
 * The signed-in chrome, replacing the phase-2 `AuthedLayout`.
 *
 * A top bar rather than the sidebar the ZNMS admin uses: the scenario editor
 * wants every pixel of width it can get (docs/08-frontend.md — full-width charts
 * beside a 64px input rail), and a permanent 240px navigation rail on the left
 * is the first thing that stops it fitting on a 1280px laptop.
 *
 * Everything wraps rather than collapsing into a hamburger. With this few
 * destinations, a second row on a narrow screen beats a menu that hides them.
 */
const NAV_LINKS: { to: string; label: string; exact?: boolean; adminOnly?: boolean }[] = [
  { to: "/", label: "Products", exact: true },
  // `/compare` is deliberately not here. It needs a selection to mean anything,
  // and a permanent link to "pick some scenarios first" is a worse door than the
  // Compare button that appears on a product's list once rows are ticked.
  { to: "/admin/allowlist", label: "Allow list", adminOnly: true },
  { to: "/admin/users", label: "Users", adminOnly: true },
  { to: "/admin/audit", label: "Audit log", adminOnly: true },
];

export function AppShell() {
  const auth = useAuth();
  const links = NAV_LINKS.filter((link) => !link.adminOnly || auth.permissions.isAdmin);

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-azure-deep focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-on-accent"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-30 border-b border-line bg-surface-strong backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-[100rem] flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5">
          <Link to="/" className="text-sm font-bold text-ink">
            {APP_NAME}
          </Link>

          <nav aria-label="Main" className="order-last flex w-full gap-1 sm:order-none sm:w-auto">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                activeOptions={{ exact: link.exact ?? false }}
                activeProps={{ className: "bg-chip-bg text-ink", "aria-current": "page" }}
                inactiveProps={{ className: "text-ink-soft" }}
                className="rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-link-bg-hover hover:text-ink"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>

      <OfflineBanner />

      {/*
        `overflow-x-clip` is the backstop against a page that scrolls sideways.

        Every wide thing in the app — the tables, the charts — already lives in
        its own `overflow-x-auto` box and scrolls inside it. But a scroll
        container's content still contributes to the *document's* scrollable
        width in Chrome, so at 400px the product detail and allow-list screens
        could be swiped 300-500px into blank page background. Nothing is out
        there; it is a void with a scrollbar.

        `clip` rather than `hidden` on purpose: `overflow-x: hidden` would make
        this a scroll container and force `overflow-y` to `auto`, which breaks
        the sticky header above and gives the page a second vertical scrollbar.
        `overflow-x: clip` leaves `overflow-y` genuinely `visible`.
      */}
      {/*
        Wider than the 1280px the rest of the app was built for. The scenario
        editor's outputs are now the whole column — four charts and a table of
        every figure in the projection — and a wide table that has to scroll
        inside its own box is a table nobody reads across. The short pages
        (products, admin) are cards and narrow tables that simply centre.
      */}
      <main id="main" className="mx-auto w-full max-w-[100rem] flex-1 overflow-x-clip px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
